import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "bg-transparent border-white/20 text-white h-12 rounded-sm focus-visible:ring-1 focus-visible:ring-[#0057B7] focus-visible:border-[#0057B7] placeholder:text-zinc-600";
const LABEL = "text-xs font-bold uppercase tracking-[0.18em] text-zinc-300";

export default function DeploymentForm() {
  const { t } = useLang();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    location: "",
    cohort_size: "",
    training_month: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.organization || !form.location || !form.cohort_size) {
      toast.error(t.f_err_required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/deployment-requests`, form);
      setDone(true);
      toast.success(t.f_ok_toast);
      setForm({ full_name: "", email: "", organization: "", location: "", cohort_size: "", training_month: "", notes: "" });
    } catch (err) {
      toast.error(t.f_err_toast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="form"
      data-testid="form-section"
      className="relative border-t border-white/10 bg-[#0B0C10] py-24 md:py-32"
    >
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[600px] rounded-full blur-[140px] opacity-15 pointer-events-none"
        style={{ background: "radial-gradient(circle, #0057B7, transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="mb-12 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#E11B22]">
            {t.form_label}
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
            {t.form_title}
          </h2>
          <p className="mt-5 text-sm sm:text-lg text-zinc-300">{t.form_sub}</p>
        </Reveal>

        {done ? (
          <Reveal className="rounded-sm border border-[#0057B7]/40 bg-[#12131C] p-10 text-center glow-blue" data-testid="form-success">
            <h3 className="font-heading text-2xl font-medium">{t.f_success_title}</h3>
            <p className="mt-3 text-zinc-300">{t.f_success_body}</p>
            <button
              onClick={() => setDone(false)}
              data-testid="form-reset"
              className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-[#4d9fff] hover:text-white transition-colors"
            >
              {t.f_reset}
            </button>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} className="grid gap-6" data-testid="deployment-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="grid gap-2">
                  <Label className={LABEL}>{t.f_name}</Label>
                  <Input data-testid="input-full-name" value={form.full_name} onChange={set("full_name")} placeholder={t.f_name_ph} className={FIELD} />
                </div>
                <div className="grid gap-2">
                  <Label className={LABEL}>{t.f_email}</Label>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder={t.f_email_ph} className={FIELD} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="grid gap-2">
                  <Label className={LABEL}>{t.f_org}</Label>
                  <Input data-testid="input-organization" value={form.organization} onChange={set("organization")} placeholder={t.f_org_ph} className={FIELD} />
                </div>
                <div className="grid gap-2">
                  <Label className={LABEL}>{t.f_location}</Label>
                  <Input data-testid="input-location" value={form.location} onChange={set("location")} placeholder={t.f_location_ph} className={FIELD} />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="grid gap-2">
                  <Label className={LABEL}>{t.f_cohort}</Label>
                  <Select value={form.cohort_size} onValueChange={set("cohort_size")}>
                    <SelectTrigger data-testid="select-cohort" className={FIELD}>
                      <SelectValue placeholder={t.f_cohort_ph} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12131C] border-white/15 text-white">
                      <SelectItem value="10-25">10–25</SelectItem>
                      <SelectItem value="26-50">26–50</SelectItem>
                      <SelectItem value="51+">51+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label className={LABEL}>{t.f_month}</Label>
                  <Input data-testid="input-training-month" value={form.training_month} onChange={set("training_month")} placeholder={t.f_month_ph} className={FIELD} />
                </div>
              </div>

              <div className="grid gap-2">
                <Label className={LABEL}>{t.f_notes}</Label>
                <Textarea
                  data-testid="input-notes"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder={t.f_notes_ph}
                  className="bg-transparent border-white/20 text-white rounded-sm min-h-[140px] focus-visible:ring-1 focus-visible:ring-[#0057B7] focus-visible:border-[#0057B7] placeholder:text-zinc-600"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="deployment-submit"
                className="group relative mt-2 overflow-hidden rounded-sm bg-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-[#0B0C10] transition-all duration-300 hover:glow-gold disabled:opacity-60"
              >
                <span className="relative z-10 flex items-center justify-center gap-3">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> {t.f_submitting}
                    </>
                  ) : (
                    <>
                      {t.f_submit}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </span>
                <span className="absolute inset-0 -translate-y-full bg-[#e6c65a] transition-transform duration-300 group-hover:translate-y-0" />
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
